/** @jsx isolateComponent */
import { useState, useEffect } from 'react';
import { css, keyframes } from '@emotion/core';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';

import { Animation, Zindexes } from '../../../theme';
import { IconButton } from '../../navigation/IconButton';

import { useUnscrollableBody } from './useUnscrollableBody';

type Props = {
  /**
   * The description of the modal modal to be read by screen readers
   * when `Modal` opens.
   */
  ariaLabel?: string;
  /**
   * Indicates whether `Modal` can be closed. When `false`,
   * no close button will render and clicking the overlay will not
   * close `Modal`. The code rendering `Modal` must manually
   * close it.
   */
  canClose?: boolean;
  /**
   * Indicates whether `document.body` can still be scrolled.
   */
  canScrollBody?: boolean;
  /**
   * The content to render inside of `Modal`.
   */
  children: React.ReactNode;
  /**
   * Function called when the modal has finished closing.
   */
  onClosed: () => void;
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const Styles = labelStyles('Modal', {
  root: css`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    // Setting height is redundant in most browsers since top/bottom are set,
    // but necessary for Safari to properly apply align-items.
    height: 100%;
    z-index: ${Zindexes.modalOverlay};
    opacity: 0;
    display: grid;
    grid-template-rows: auto 1fr;

    @media screen and (min-width: 650px) {
      align-items: center;
      justify-items: center;
    }
  `,

  rootIn: css`
    animation: ${fadeIn} ${Animation.transitionDuration}
      ${Animation.transitionTimingFunction} forwards;
  `,

  rootOut: css`
    animation: ${fadeOut} ${Animation.transitionDuration}
      ${Animation.transitionTimingFunction} forwards;
  `,

  overlay: css`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.8);
    z-index: 1;
  `,

  closeButtonWrapper: css`
    position: relative;
    z-index: 3;
    justify-self: end;
    margin: 20px;
  `,

  modal: css`
    position: relative;
    z-index: 2;
    display: flex;
    min-height: 0;
  `,
});

export const Modal = (props: Props) => {
  // Normalize `true`, `undefined`, `null`.
  const canClose = props.canClose !== false;
  const [isOpen, setIsOpen] = useState(true);

  useUnscrollableBody({ shouldAllowScroll: props.canScrollBody === true });

  // Handle close request and kick off closing animation.
  const onClose = () => {
    if (canClose) {
      setIsOpen(false);
    }
  };

  // Once the closing animation has completed, call `onClosed`.
  const onAnimationEnd = () => {
    if (isOpen === false) {
      props.onClosed();
    }
  };

  // Close `Modal` when escape key is pressed. No need
  // to worry about `canClose` here since it's handled elsewhere.
  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keyup', onEscape);
    return () => {
      window.removeEventListener('keyup', onEscape);
    };
  });

  const overlay = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div css={[Styles.overlay]} data-testid="ModalOverlay" onClick={onClose} />
  );

  const closeBtn = canClose ? (
    <div css={Styles.closeButtonWrapper}>
      <IconButton
        ariaLabel={['Close modal', props.ariaLabel].filter(Boolean).join(': ')}
        color="greyAlt"
        icon="formX"
        isPill={true}
        label="Close"
        onClick={onClose}
        tooltipPosition="west"
      />
    </div>
  ) : (
    <div />
  );

  const modal = (
    <div
      aria-label={props.ariaLabel}
      aria-live="polite"
      aria-modal="true"
      css={Styles.modal}
      role="dialog"
    >
      {props.children}
    </div>
  );

  return (
    <div
      css={[Styles.root, isOpen ? Styles.rootIn : Styles.rootOut]}
      data-testid="Modal"
      onAnimationEnd={onAnimationEnd}
    >
      {overlay}
      {closeBtn}
      {modal}
    </div>
  );
};
